import { useEffect, useState } from 'react';
import { getAllOptions, useCatalog } from '@/api/catalog';
import { api, ApiError } from '@/api/client';
import type { Page } from '@/api/types';
import { Icon } from '@/components/Icon';
import { num, qty } from '@/lib/format';
import { adjustInventory } from './api';
import type { Warehouse, WarehouseStock } from './types';
import s from './InventoryAdjustDialog.module.css';

type Product = { id: string; code: string; name: string };

export function InventoryAdjustDialog({ warehouses: initialWarehouses, onClose, onSuccess }: { warehouses: Warehouse[]; onClose: () => void; onSuccess: () => void }) {
  const [mode, setMode] = useState<'set' | 'delta'>('set');
  const warehouseCatalog = useCatalog<Warehouse>('/api/warehouses');
  const warehouses = warehouseCatalog.data;
  const optionsError = warehouseCatalog.error;
  const optionsLoading = warehouseCatalog.loading;
  const retryOptions = warehouseCatalog.reload;
  const [warehouseId, setWarehouseId] = useState(initialWarehouses.find((item) => item.isDefaultInbound)?.id || initialWarehouses[0]?.id || '');
  const [productId, setProductId] = useState('');
  const [keyword, setKeyword] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [productError, setProductError] = useState('');
  const [current, setCurrent] = useState<number | null>(null);
  const [stockError, setStockError] = useState('');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => { if (!warehouseCatalog.loading && !warehouseCatalog.error) setWarehouseId((current) => current || warehouses.find((item) => item.isDefaultInbound)?.id || warehouses[0]?.id || ''); }, [warehouses, warehouseCatalog.loading, warehouseCatalog.error]);

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      void getAllOptions<Product>('/api/products', { scope: 'inventory-options', keyword: keyword.trim() || undefined }, controller.signal)
        .then((result) => { if (!controller.signal.aborted) { setProducts(result); setProductError(''); } })
        .catch((cause: Error) => { if (!controller.signal.aborted) setProductError(cause.message); });
    }, 160);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [keyword]);

  useEffect(() => {
    setCurrent(null);
    setStockError('');
    if (!warehouseId || !productId) return;
    const controller = new AbortController();
    void api.get<Page<WarehouseStock>>('/api/inventory/stocks', { warehouseId, productId, pageSize: 1 }, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setCurrent(num(result.items[0]?.currentQuantity || 0)); })
      .catch((cause: Error) => { if (!controller.signal.aborted) setStockError(cause.message); });
    return () => controller.abort();
  }, [warehouseId, productId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const amount = Number(quantity);
  const after = current === null || !Number.isFinite(amount) ? null : mode === 'set' ? amount : current + amount;
  const selectedProduct = products.find((item) => item.id === productId);
  const valid = !optionsLoading && !optionsError && Boolean(productId && warehouseId && note.trim() && current !== null && Number.isFinite(amount) && (mode === 'set' ? amount >= 0 : amount !== 0) && after !== null && after >= 0);

  async function submit() {
    if (!valid || saving || uncertain) return;
    setSaving(true);
    setError('');
    try {
      await adjustInventory({ productId, warehouseId, mode, quantity: amount, note: note.trim() });
      setDone(true);
      onSuccess();
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '调整失败');
    } finally {
      setSaving(false);
    }
  }

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label="库存调整">
      <header className={s.head}><button className={s.closeButton} type="button" onClick={onClose} disabled={saving} aria-label="关闭"><span className={s.desktopClose}><Icon name="x" size={18} /></span><span className={s.mobileCancel}>取消</span></button><h2>库存调整</h2><span className={s.headSpacer} aria-hidden="true" /></header>
      {done ? <div className={s.done}><Icon name="check" size={24} /><b>库存已调整</b><p>已写入库存流水，可在出入库流水中复核。</p><button type="button" onClick={onClose}>完成</button></div> : <>
        <div className={s.body}>{optionsError && <p className={s.error} role="alert">资料加载失败：{optionsError.message} <button type="button" onClick={retryOptions}>重试</button></p>}
          <p className={s.hint}>用于盘点后把账面数改成实际数。会写 <b>手工调整</b> 流水。</p>
          <fieldset className={s.mode}><legend><em>*</em> 调整方式</legend><label className={mode === 'set' ? s.modeSelected : ''}><input type="radio" checked={mode === 'set'} onChange={() => { setMode('set'); setQuantity(''); }} /><span>设为指定数量</span></label><label className={mode === 'delta' ? s.modeSelected : ''}><input type="radio" checked={mode === 'delta'} onChange={() => { setMode('delta'); setQuantity(''); }} /><span>在当前基础上增减</span></label></fieldset>
          <div className={s.sectionLabel}>盘点对象</div>
          <div className={s.grid}>
            <label><span><em>*</em> 仓库</span><select value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">请选择仓库</option>{warehouses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
            <label className={`${s.productField} ${productId ? s.hasProduct : ''}`}><span><em>*</em> 产品</span><input type="search" value={selectedProduct ? `${selectedProduct.name} · ${selectedProduct.code}` : keyword} onFocus={() => { if (productId) { setProductId(''); setKeyword(''); setQuantity(''); } }} onChange={(event) => { setKeyword(event.target.value); setProductId(''); }} placeholder="搜索编号或名称" /><select value={productId} onChange={(event) => setProductId(event.target.value)}><option value="">请选择产品</option>{products.map((item) => <option key={item.id} value={item.id}>{item.name} · {item.code}</option>)}</select>{productError && <small className={s.error}>{productError}</small>}</label>
          </div>
          <div className={s.grid3}>
            <label>账面现有<output>{current === null ? '—' : qty(current)}</output></label>
            <label><span><em>*</em> {mode === 'set' ? '实际数量' : '增减数量'}</span><div className={s.quantityStepper}><button type="button" aria-label="减少调整数量" onClick={() => setQuantity(String(mode === 'set' ? Math.max(0, amount - 1) : amount - 1))}>−</button><input type="number" step="0.001" min={mode === 'set' ? 0 : undefined} value={quantity} onChange={(event) => setQuantity(event.target.value)} placeholder={mode === 'set' ? '盘点后实物数量' : '增加填正数，减少填负数'} /><button type="button" aria-label="增加调整数量" onClick={() => setQuantity(String(amount + 1))}>＋</button></div></label>
            <label>{mode === 'set' ? '差异' : '调整后现有'}<output className={after !== null && (mode === 'set' ? after - (current || 0) : after) < 0 ? s.bad : undefined}>{after === null ? '—' : mode === 'set' ? qty(after - (current || 0)) : qty(after)}</output></label>
          </div>
          {stockError && <p className={s.error}>{stockError}</p>}
          {after !== null && after < 0 && <p className={s.error}>盘点后现有库存不能小于 0。</p>}
          <label className={s.reason}><span><em>*</em> 调整原因</span><input className={s.desktopReasonInput} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：月度盘点差异，实物已不在库" /><textarea className={s.mobileReasonInput} rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：月度盘点差异，实物已不在库" /><small>原因会写进流水备注，操作日志同步记录。</small></label>
          {error && <p className={s.error} role="alert">{error}{uncertain && '。请求结果未确认，请先核对库存流水，暂勿重复提交。'}</p>}
        </div>
        <footer className={s.foot}><button type="button" onClick={onClose} disabled={saving}>取消</button><button type="button" className={s.primary} disabled={!valid || saving || uncertain} onClick={() => void submit()}>{saving ? '正在调整…' : '确认调整'}</button></footer>
      </>}
    </section>
  </div>;
}
